import React, { useMemo } from 'react'
import LinkIcon from '@mui/icons-material/Link'
import RouteIcon from '@mui/icons-material/Route'
import { DatePosition, getPercentXForDate } from '../hooks/useDatePositions'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { GanttColors } from '../constants'
import { Dependency } from '../GanttDependency/DependencyContext'
import { CriticalPathLinkData } from '../GanttCriticalPath/CriticalPathContext'
import { FallbackLinkInfo } from '../utils/visibilityFilters'

const ICON_SIZE = 10
const CIRCLE_SIZE = 16

// Scheduled bar geometry (must match SliderContainer + ganttSliderStyles)
// Regular items: center = 50% - 3.5px, barHeight = 20px
// Summary items: center = 50%, barHeight = 32px
const getBarEdgePositions = (isSummaryRow: boolean) => {
  const setShift = isSummaryRow ? 0 : 3.5
  const barHalfHeight = isSummaryRow ? 16 : 10
  return {
    top: `calc(50% - ${setShift + barHalfHeight}px)`,
    bottom: `calc(50% - ${setShift - barHalfHeight}px)`,
  }
}

type FallbackLinkIndicatorProps = {
  datePositions: DatePosition[]
  fallbackDeps: FallbackLinkInfo<Dependency>[]
  fallbackCPs: FallbackLinkInfo<CriticalPathLinkData>[]
  currentRowIndex: number
  isSummaryRow: boolean
  onDepClick: (
    fallbackInfo: FallbackLinkInfo<Dependency>,
    event: React.MouseEvent
  ) => void
  onCpClick: (
    fallbackInfo: FallbackLinkInfo<CriticalPathLinkData>,
    event: React.MouseEvent
  ) => void
}

type IconPlacement = {
  key: string
  percentX: number
  topPosition: string
  color: string
  type: 'dependency' | 'criticalPath'
  onClick: (event: React.MouseEvent) => void
}

export const FallbackLinkIndicator = React.memo<FallbackLinkIndicatorProps>(
  ({
    datePositions,
    fallbackDeps,
    fallbackCPs,
    currentRowIndex,
    isSummaryRow,
    onDepClick,
    onCpClick,
  }) => {
    const icons = useMemo(() => {
      const result: IconPlacement[] = []
      const barEdge = getBarEdgePositions(isSummaryRow)

      // Dependency fallback icons (positioned at bar center, matching DependencyLines)
      for (const info of fallbackDeps) {
        const { link, from, to } = info

        if (from.rowIndex === currentRowIndex && !from.isFallback) {
          // From-side icon: position at fromEndDate (right edge of bar)
          const date = link.fromEndDate
            ? CalendarDateVO.of(link.fromEndDate)
            : null
          if (date) {
            const x = getPercentXForDate(datePositions, date, 'right')
            if (x !== null) {
              result.push({
                key: `dep-from-${link.uuid}`,
                percentX: x,
                topPosition: '50%',
                color: link.color || GanttColors.DEPENDENCY_LINE,
                type: 'dependency',
                onClick: e => onDepClick(info, e),
              })
            }
          }
        }

        if (to.rowIndex === currentRowIndex && !to.isFallback) {
          // To-side icon: position at toStartDate (left edge of bar)
          const date = link.toStartDate
            ? CalendarDateVO.of(link.toStartDate)
            : null
          if (date) {
            const x = getPercentXForDate(datePositions, date, 'left')
            if (x !== null) {
              result.push({
                key: `dep-to-${link.uuid}`,
                percentX: x,
                topPosition: '50%',
                color: link.color || GanttColors.DEPENDENCY_LINE,
                type: 'dependency',
                onClick: e => onDepClick(info, e),
              })
            }
          }
        }
      }

      // Critical path fallback icons (positioned at bar edge, matching CriticalPathLines)
      for (const info of fallbackCPs) {
        const { link, from, to } = info

        if (from.rowIndex === currentRowIndex && !from.isFallback) {
          const x = getPercentXForDate(datePositions, link.fromDate, 'center')
          if (x !== null) {
            // From-side: line exits toward the to-side.
            // When to is a fallback, the ancestor's rowIndex may not reflect the
            // actual endpoint's direction (e.g. ancestor is above but actual
            // endpoint is below). Default to "below" when the endpoint is a
            // fallback, since critical path lines typically go downward.
            const isToBelow =
              to.isFallback || to.rowIndex < 0 || to.rowIndex >= currentRowIndex
            result.push({
              key: `cp-from-${link.uuid}`,
              percentX: x,
              topPosition: isToBelow ? barEdge.bottom : barEdge.top,
              color: link.color || GanttColors.CRITICAL_PATH_LINE,
              type: 'criticalPath',
              onClick: e => onCpClick(info, e),
            })
          }
        }

        if (to.rowIndex === currentRowIndex && !to.isFallback) {
          const x = getPercentXForDate(datePositions, link.toDate, 'center')
          if (x !== null) {
            // To-side: line arrives from the from-side.
            // When from is a fallback, the ancestor's rowIndex may not reflect
            // the actual endpoint's direction. Default to "above" when the
            // endpoint is a fallback, since critical path lines typically
            // arrive from above.
            const isFromAbove =
              from.isFallback ||
              from.rowIndex < 0 ||
              from.rowIndex <= currentRowIndex
            result.push({
              key: `cp-to-${link.uuid}`,
              percentX: x,
              topPosition: isFromAbove ? barEdge.top : barEdge.bottom,
              color: link.color || GanttColors.CRITICAL_PATH_LINE,
              type: 'criticalPath',
              onClick: e => onCpClick(info, e),
            })
          }
        }
      }

      return result
    }, [
      datePositions,
      fallbackDeps,
      fallbackCPs,
      currentRowIndex,
      isSummaryRow,
      onDepClick,
      onCpClick,
    ])

    if (icons.length === 0) return null

    return (
      <>
        {icons.map(icon => (
          <div
            key={icon.key}
            style={{
              position: 'absolute',
              left: `${icon.percentX}%`,
              top: icon.topPosition,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'auto',
              cursor: 'pointer',
              zIndex: 5,
              width: CIRCLE_SIZE,
              height: CIRCLE_SIZE,
              borderRadius: '50%',
              backgroundColor: 'white',
              border: `1.5px solid ${icon.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={icon.onClick}
          >
            {icon.type === 'dependency' ? (
              <LinkIcon
                sx={{
                  fontSize: ICON_SIZE,
                  color: icon.color,
                }}
              />
            ) : (
              <RouteIcon
                sx={{
                  fontSize: ICON_SIZE,
                  color: icon.color,
                }}
              />
            )}
          </div>
        ))}
      </>
    )
  }
)

FallbackLinkIndicator.displayName = 'FallbackLinkIndicator'
